import {Controller} from '@hotwired/stimulus';
import {DirectUpload} from '@rails/activestorage';
import {getDocumentMeta, removeElement} from '../helpers/documentHelper';
import Dropzone from 'dropzone';

Dropzone.autoDiscover = false;

// Connects to data-controller="dropzone"
export default class extends Controller {
  static targets = ['input', 'previewsContainer', 'previewTemplate'];
  static values = {
    maxFiles: Number,
    maxSize: Number, // in bytes
    allowedTypes: Array,
    uploadUrl: String,
    uploadMethod: String,
    addRemoveLinks: Boolean,
    paramName: String,
    clickable: Boolean,
    parallelUploads: Number,
    autoQueue: Boolean,
    createImageThumbnails: Boolean,
  };

  connect() {
    this.prepareSettings();
    console.log(this.settings);
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();
    Dropzone.autoDiscover = false; // necessary quirk for Dropzone error in console
  }

  prepareSettings() {
    this.settings = {
      url: this.uploadUrlValue,
      maxFiles: this.maxFilesValue || 1,
      maxFileSize: this.maxSizeValue || 10 * 1024 * 1024, // default to 10MB
      acceptedFiles: (this.allowedTypesValue || []).join(','),
      method: this.uploadMethodValue || 'POST',
      addRemoveLinks: this.hasAddRemoveLinksValue ? this.addRemoveLinksValue : true,
      paramName: this.hasParamNameValue ? this.paramNameValue : 'file',
      clickable: this.hasClickableValue ? this.clickableValue : true,
      parallelUploads: this.parallelUploadsValue || 1,
      autoQueue: this.hasAutoQueueValue ? this.autoQueueValue : false,
      createImageThumbnails: this.hasCreateImageThumbnailsValue ? this.createImageThumbnailsValue : false,
      headers: {'X-CSRF-Token': getDocumentMeta('csrf-token')},
    };
  }

  bindEvents() {
    this.dropZone.on('addedfile', (file) => {
      setTimeout(() => {
        file.accepted && createDirectUploadController(this, file).start();
      }, 500);
    });

    this.dropZone.on('removedfile', (file) => {
      file.controller && removeElement(file.controller.hiddenInput);
    });

    this.dropZone.on('canceled', (file) => {
      file.controller && file.controller.xhr.abort();
    });
  }

  hideFileInput() {
    if (!this.hasInputTarget) return;
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = 'none';
  }
}

class DirectUploadController {
  directUpload = null;
  source = null;
  file = null;
  hiddenInput = null;
  element = null;
  xhr = null;

  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.settings.url, this);
    this.source = source;
    this.file = file;
  }

  start() {
    this.file.controller = this;
    this.hiddenInput = this.createHiddenInput();
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
      } else {
        this.hiddenInput.value = attributes.signed_id;
        this.emitDropzoneSuccess();
      }
    });
  }

  createHiddenInput() {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = this.source.inputTarget.name || this.source.settings.paramName;
    this.file.previewTemplate.append(input);
    // insertElement(input, this.source.inputTarget, 'after')
    return input;
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr);
    this.emitDropzoneUploading();
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr;
    this.xhr.upload.addEventListener('progress', (event) => this.uploadRequestDidProgress(event));
  }

  uploadRequestDidProgress(event) {
    this.element = this.source.element;
    window.d = this.file.previewTemplate;
    const progress = (event.loaded / event.total) * 100;
    const uploadEl = this.file.previewTemplate.querySelector('.dz-upload');
    if (uploadEl) {
      uploadEl.style.width = `${progress}%`;
    }
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit('processing', this.file);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit('error', this.file, error);
    this.source.dropZone.emit('complete', this.file);
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit('success', this.file);
    this.source.dropZone.emit('complete', this.file);
  }
}

function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller);
}

function createDropZone(controller) {
  const options = {};
  if (controller.hasPreviewsContainerTarget) {
    options.previewsContainer = controller.previewsContainerTarget;
  }
  if (controller.hasPreviewTemplateTarget) {
    options.previewTemplate = controller.previewTemplateTarget.innerHTML;
  }
  return new Dropzone(controller.element, {
    url: controller.settings.url,
    method: controller.settings.method,
    headers: controller.settings.headers,
    maxFiles: controller.settings.maxFiles,
    maxFilesize: controller.settings.maxFileSize,
    acceptedFiles: controller.settings.acceptedFiles,
    addRemoveLinks: controller.settings.addRemoveLinks,
    autoQueue: controller.settings.autoQueue,
    clickable: controller.settings.clickable,
    parallelUploads: controller.settings.parallelUploads,
    dictRemoveFile: "Usuń",
    ...options,
  });
}
